
import React, { useState } from 'react';
import './Assessment.css';

/* ══════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════ */
const QUESTIONS = [
  { id: 1, text: "How clearly defined are operational roles and accountability structures?", options: [
    { text: "Roles are unclear and inconsistent", score: 2 },
    { text: "Partial accountability exists", score: 5 },
    { text: "Roles defined across major functions", score: 8 },
    { text: "Enterprise-wide governance established", score: 10 }
  ]},
  { id: 2, text: "How effectively do teams collaborate across business functions?", options: [
    { text: "Highly siloed operations", score: 2 },
    { text: "Limited collaboration exists", score: 5 },
    { text: "Moderate cross-functional collaboration", score: 8 },
    { text: "Fully integrated operating model", score: 10 }
  ]},
  { id: 3, text: "How agile is your organization in responding to operational or market changes?", options: [
    { text: "Slow and reactive", score: 2 },
    { text: "Limited adaptability", score: 5 },
    { text: "Agile in selected functions", score: 8 },
    { text: "Enterprise-wide adaptive operating model", score: 10 }
  ]},
  { id: 4, text: "How effectively are strategic decisions translated into operational execution?", options: [
    { text: "Significant execution gaps", score: 2 },
    { text: "Inconsistent execution alignment", score: 5 },
    { text: "Moderate operational alignment", score: 8 },
    { text: "Strong enterprise execution discipline", score: 10 }
  ]},
  { id: 5, text: "How mature are your performance measurement and accountability mechanisms?", options: [
    { text: "No formal measurement structure", score: 2 },
    { text: "KPIs tracked inconsistently", score: 5 },
    { text: "Structured performance management exists", score: 8 },
    { text: "Enterprise-wide performance governance", score: 10 }
  ]},
  { id: 6, text: "How scalable is your current operating model?", options: [
    { text: "Difficult to scale operations", score: 2 },
    { text: "Limited scalability", score: 5 },
    { text: "Moderate scalability across functions", score: 8 },
    { text: "Highly scalable enterprise operating structure", score: 10 }
  ]},
  { id: 7, text: "How effectively are operational risks managed across the organization?", options: [
    { text: "Reactive risk management only", score: 2 },
    { text: "Basic operational controls exist", score: 5 },
    { text: "Structured operational risk management", score: 8 },
    { text: "Enterprise-wide proactive risk governance", score: 10 }
  ]},
  { id: 8, text: "How integrated are digital capabilities into your operating model?", options: [
    { text: "Minimal digital integration", score: 2 },
    { text: "Digital tools used selectively", score: 5 },
    { text: "Moderate digital enablement", score: 8 },
    { text: "Fully digitally enabled operating model", score: 10 }
  ]},
  { id: 9, text: "How effective is leadership alignment across operational priorities?", options: [
    { text: "Frequent leadership misalignment", score: 2 },
    { text: "Alignment exists inconsistently", score: 5 },
    { text: "Leadership aligned on major priorities", score: 8 },
    { text: "Strong enterprise-wide alignment", score: 10 }
  ]},
  { id: 10, text: "How prepared is your operating model for future business transformation?", options: [
    { text: "Not prepared for transformation", score: 2 },
    { text: "Limited future-readiness planning", score: 5 },
    { text: "Moderate transformation preparedness", score: 8 },
    { text: "Fully transformation-ready operating model", score: 10 }
  ]}
];

const QUALIFICATION_OPTIONS = {
  priority: ["Cross-functional inefficiencies", "Role and accountability gaps", "Slow decision-making", "Scalability limitations", "Execution inconsistency"],
  timeline: ["Long-term (After 12 months)", "Mid-term (6-12 months)", "Near-term (3-6 months)", "Immediate (0-3 months)"],
  investment: ["Below $50K", "$50K-$100K", "$100K-$500K", "$500K+"],
  consultation: [
    "Yes - We are actively exploring transformation initiatives",
    "Exploring - We would like expert recommendations and next steps",
    "Evaluating - We are currently evaluating priorities internally",
    "Early Assessment - We are only assessing current maturity",
  ],
};

const RESULT_DATA = {
  Foundational: {
    label: "Foundational", range: "0 – 25", color: "foundational",
    desc: "Your organization is in the early stages of operational maturity and may require foundational alignment across governance, execution, and collaboration structures.",
    actions: ["Establish clear role definitions and accountability structures", "Improve cross-functional collaboration", "Develop basic performance measurement frameworks"],
  },
  Emerging: {
    label: "Emerging", range: "26 – 50", color: "emerging",
    desc: "Your organization demonstrates growing operational maturity with opportunities to improve alignment, scalability, and governance consistency.",
    actions: ["Strengthen governance frameworks", "Scale successful operational practices", "Enhance cross-functional coordination"],
  },
  Advancing: {
    label: "Advancing", range: "51 – 75", color: "advancing",
    desc: "Your organization demonstrates strong operational alignment with growing readiness for scalable enterprise transformation.",
    actions: ["Accelerate enterprise-wide operational integration", "Embed digital capabilities into core processes", "Develop proactive risk management"],
  },
  "Transformation Ready": {
    label: "Transformation Ready", range: "76 – 100", color: "ready",
    desc: "Your organization demonstrates advanced operational maturity with strong governance, agility, scalability, and enterprise execution capabilities.",
    actions: ["Drive continuous operational innovation", "Lead industry best practices", "Scale transformation across the enterprise"],
  },
};

/* ══════════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════════ */
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SpinIcon = () => (
  <svg className="ar-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

/* ══════════════════════════════════════════════════════════
   STEP INDICATOR
══════════════════════════════════════════════════════════ */
const STEPS = ["Your Details", "Diagnostic", "Qualification", "Results"];

function StepBar({ current }) {
  return (
    <div className="ar-stepbar">
      {STEPS.map((label, i) => {
        const idx    = i + 1;
        const done   = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={i}>
            <div className={`ar-step${active ? " ar-step--active" : ""}${done ? " ar-step--done" : ""}`}>
              <div className="ar-step__dot">
                {done ? <CheckIcon /> : <span>{idx}</span>}
              </div>
              <span className="ar-step__label">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`ar-step__line${done ? " ar-step__line--done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
function OperatingModel() {
  const [step,          setStep]          = useState(1);
  const [answers,       setAnswers]       = useState({});
  const [qIndex,        setQIndex]        = useState(0);
  const [qualification, setQualification] = useState({});
  const [result,        setResult]        = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [errors,        setErrors]        = useState({});
  const [userInfo, setUserInfo] = useState({
    fullName: '', companyName: '', role: '',
    industry: '', organizationSize: '', email: '', phoneNumber: '',
  });

  /* ── Helpers ── */
  const getCategory = (score) => {
    if (score <= 25) return 'Foundational';
    if (score <= 50) return 'Emerging';
    if (score <= 75) return 'Advancing';
    return 'Transformation Ready';
  };

  const totalScore    = () => Object.values(answers).reduce((s, v) => s + v, 0);
  const scorePercent  = () => Math.round((totalScore() / 100) * 100);
  const currentQ      = QUESTIONS[qIndex];

  /* ── Handlers ── */
  const handleUserInfo = (field, value) => {
    setUserInfo({ ...userInfo, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const validateStep1 = () => {
    const req = { fullName: 'Full name', companyName: 'Company name', role: 'Role', email: 'Email', phoneNumber: 'Phone number' };
    const newErrors = {};
    Object.entries(req).forEach(([k, label]) => {
      if (!userInfo[k]?.trim()) newErrors[k] = `${label} is required`;
    });
    const emailRx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (userInfo.email && !emailRx.test(userInfo.email)) newErrors.email = 'Enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAnswer = (questionId, score) => setAnswers({ ...answers, [questionId]: score });

  const handleNext = () => {
    if (qIndex < QUESTIONS.length - 1) setQIndex(qIndex + 1);
    else setStep(3);
  };

  const handleQualification = (field, value) => setQualification({ ...qualification, [field]: value });

  const handleSubmit = async () => {
    setSubmitting(true);
    const score    = totalScore();
    const category = getCategory(score);

    const payload = {
      assessmentType: 'operating-model',
      ...userInfo,
      answers: Object.entries(answers).map(([q, sc]) => ({
        questionNumber: parseInt(q),
        answer: QUESTIONS.find(qq => qq.id === parseInt(q))?.options.find(o => o.score === sc)?.text || '',
        score: sc,
      })),
      qualification: {
        priority:             qualification.priority || '',
        timeline:             qualification.timeline || '',
        investment:           qualification.investment || '',
        consultationInterest: qualification.consultation || '',
        additionalInfo:       qualification.additionalInfo || '',
      },
      totalScore: score,
      resultCategory: category,
    };

    try {
      const res  = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/submit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ totalScore: score, resultCategory: category });
        setStep(4);
      } else {
        alert('Error: ' + (data.message || 'Submission failed'));
      }
    } catch (err) {
      console.error(err);
      alert('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ══════════════════════════════════════════════════════
     STEP 4 — RESULT
  ══════════════════════════════════════════════════════ */
  if (step === 4 && result) {
    const rd = RESULT_DATA[result.resultCategory];
    return (
      <div className="ar-page">
        <div className="ar-hero ar-hero--result">
          <div className="ar-hero__inner">
            <p className="ar-eyebrow">Operating Model Design Assessment</p>
            <h1 className="ar-hero__title">Your Results</h1>
          </div>
        </div>

        <div className="ar-body">
          <div className={`ar-result ar-result--${rd.color}`}>

            <div className="ar-result__top">
              <div className="ar-score-ring">
                <svg viewBox="0 0 120 120" className="ar-ring-svg">
                  <circle cx="60" cy="60" r="50" className="ar-ring-bg" />
                  <circle cx="60" cy="60" r="50" className="ar-ring-fill"
                    style={{ strokeDashoffset: `${314 - (314 * scorePercent()) / 100}` }} />
                </svg>
                <div className="ar-ring-label">
                  <span className="ar-ring-score">{result.totalScore}</span>
                  <span className="ar-ring-max">/100</span>
                </div>
              </div>

              <div className="ar-result__meta">
                <span className={`ar-badge ar-badge--${rd.color}`}>{rd.label}</span>
                <p className="ar-result__range">Score range: {rd.range}</p>
                <p className="ar-result__desc">{rd.desc}</p>
              </div>
            </div>

            <div className="ar-result__actions-block">
              <h3 className="ar-result__actions-title">Recommended Next Steps</h3>
              <ul className="ar-result__actions-list">
                {rd.actions.map((a, i) => (
                  <li key={i} className="ar-result__action">
                    <span className="ar-result__action-dot"><CheckIcon /></span>
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            <div className="ar-result__cta">
              <a href="/contact" className="ar-btn ar-btn--gold">
                Talk to a BaiRees Advisor <ArrowRight />
              </a>
              <button onClick={() => window.location.reload()} className="ar-btn ar-btn--ghost">
                Retake Assessment
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     SHARED WRAPPER
  ══════════════════════════════════════════════════════ */
  return (
    <div className="ar-page">

      {/* Hero */}
      <div className="ar-hero">
        <div className="ar-hero__inner">
          <p className="ar-eyebrow">BaiRees Global Advisory</p>
          <h1 className="ar-hero__title">Operating Model Design Assessment</h1>
          <p className="ar-hero__sub">
            Evaluate how effectively your organization's operating model supports execution alignment,
            governance, agility, and scalable growth.
          </p>
        </div>
      </div>

      {/* Step bar */}
      <div className="ar-stepbar-wrap">
        <div className="ar-stepbar-inner">
          <StepBar current={step} />
        </div>
      </div>

      <div className="ar-body">

        {/* ══ STEP 1 — PARTICIPANT INFO ══════════════════ */}
        {step === 1 && (
          <div className="ar-card">
            <div className="ar-card__header">
              <h2 className="ar-card__title">Participant Information</h2>
              <p className="ar-card__sub">Tell us about yourself so we can personalise your results.</p>
            </div>

            <div className="ar-form-grid">
              {[
                { field: 'fullName',    label: 'Full Name',      type: 'text',  placeholder: 'Your full name' },
                { field: 'companyName', label: 'Company Name',   type: 'text',  placeholder: 'Your company' },
                { field: 'email',       label: 'Business Email', type: 'email', placeholder: 'you@company.com' },
                { field: 'phoneNumber', label: 'Phone Number',   type: 'tel',   placeholder: '+1 (555) 000-0000' },
              ].map(({ field, label, type, placeholder }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label} <span className="ar-req">*</span></label>
                  <input
                    className={`ar-field__input${errors[field] ? ' ar-field__input--error' : ''}`}
                    type={type}
                    value={userInfo[field]}
                    onChange={(e) => handleUserInfo(field, e.target.value)}
                    placeholder={placeholder}
                  />
                  {errors[field] && <span className="ar-field__error">{errors[field]}</span>}
                </div>
              ))}

              {[
                { field: 'role', label: 'Role', opts: ['Founder/CEO', 'C-Suite Executive (CXO)', 'Technology & AI Leader', 'Operations & Transformation Leader', 'Business Unit / P&L Head'] },
                { field: 'industry', label: 'Industry', opts: ['Technology', 'Healthcare', 'Manufacturing', 'Retail', 'Finance', 'Education', 'Logistics', 'Pharma', 'Consulting', 'Other'] },
                { field: 'organizationSize', label: 'Organization Size', opts: ['1-50 Employees', '50-100 Employees', '100-500 Employees', '500-1000 Employees', '1000+ Employees'] },
              ].map(({ field, label, opts }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label} <span className="ar-req">*</span></label>
                  <select
                    className={`ar-field__input ar-field__select${errors[field] ? ' ar-field__input--error' : ''}`}
                    value={userInfo[field]}
                    onChange={(e) => handleUserInfo(field, e.target.value)}
                  >
                    <option value="">Select {label.toLowerCase()}</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {errors[field] && <span className="ar-field__error">{errors[field]}</span>}
                </div>
              ))}
            </div>

            <div className="ar-card__footer">
              <button className="ar-btn ar-btn--primary" onClick={() => {
                if (validateStep1()) setStep(2);
              }}>
                Start Diagnostic <ArrowRight />
              </button>
            </div>
          </div>
        )}

        {/* ══ STEP 2 — QUESTIONS ════════════════════════ */}
        {step === 2 && (
          <div className="ar-card">

            <div className="ar-progress">
              <div className="ar-progress__bar" style={{ width: `${(qIndex / QUESTIONS.length) * 100}%` }} />
            </div>
            <div className="ar-progress__meta">
              <span className="ar-progress__label">Question {qIndex + 1} of {QUESTIONS.length}</span>
              <span className="ar-progress__pct">{Math.round((qIndex / QUESTIONS.length) * 100)}% complete</span>
            </div>

            <div className="ar-question">
              <p className="ar-question__num">Q{qIndex + 1}</p>
              <h2 className="ar-question__text">{currentQ.text}</h2>
            </div>

            <div className="ar-options">
              {currentQ.options.map((opt, idx) => (
                <label
                  key={idx}
                  className={`ar-option${answers[currentQ.id] === opt.score ? ' ar-option--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`q${currentQ.id}`}
                    value={opt.score}
                    checked={answers[currentQ.id] === opt.score}
                    onChange={() => handleAnswer(currentQ.id, opt.score)}
                    className="ar-option__radio"
                  />
                  <span className="ar-option__marker">
                    {answers[currentQ.id] === opt.score ? <CheckIcon /> : <span>{idx + 1}</span>}
                  </span>
                  <span className="ar-option__text">{opt.text}</span>
                </label>
              ))}
            </div>

            <div className="ar-card__footer ar-card__footer--between">
              {qIndex > 0 ? (
                <button className="ar-btn ar-btn--ghost" onClick={() => setQIndex(qIndex - 1)}>← Back</button>
              ) : (
                <button className="ar-btn ar-btn--ghost" onClick={() => setStep(1)}>← Back</button>
              )}
              <button
                className="ar-btn ar-btn--primary"
                onClick={handleNext}
                disabled={!answers[currentQ.id]}
              >
                {qIndex < QUESTIONS.length - 1 ? <>Next Question <ArrowRight /></> : <>Continue <ArrowRight /></>}
              </button>
            </div>
          </div>
        )}

        {/* ══ STEP 3 — QUALIFICATION ════════════════════ */}
        {step === 3 && (
          <div className="ar-card">
            <div className="ar-card__header">
              <h2 className="ar-card__title">Qualification Questions</h2>
              <p className="ar-card__sub">Help us tailor our recommendations to your specific context.</p>
            </div>

            <div className="ar-qual-grid">
              {[
                { field: 'priority',     label: 'Which operational challenge currently impacts your organization the most?', opts: QUALIFICATION_OPTIONS.priority },
                { field: 'timeline',     label: 'What is your expected timeline for transformation initiatives?', opts: QUALIFICATION_OPTIONS.timeline },
                { field: 'investment',   label: 'What level of investment is your organization considering?', opts: QUALIFICATION_OPTIONS.investment },
                { field: 'consultation', label: 'Would you like a strategic consultation based on your results?', opts: QUALIFICATION_OPTIONS.consultation },
              ].map(({ field, label, opts }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label}</label>
                  <select
                    className="ar-field__input ar-field__select"
                    value={qualification[field] || ''}
                    onChange={(e) => handleQualification(field, e.target.value)}
                  >
                    <option value="">Select an option</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}

              <div className="ar-field ar-field--full">
                <label className="ar-field__label">Any additional information you would like to share?</label>
                <textarea
                  className="ar-field__input ar-field__textarea"
                  rows={4}
                  placeholder="Optional — share any context that would help us provide better recommendations…"
                  value={qualification.additionalInfo || ''}
                  onChange={(e) => handleQualification('additionalInfo', e.target.value)}
                />
              </div>
            </div>

            <div className="ar-card__footer ar-card__footer--between">
              <button className="ar-btn ar-btn--ghost" onClick={() => setStep(2)}>← Back</button>
              <button className="ar-btn ar-btn--primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><SpinIcon /> Submitting…</> : <>Submit Diagnostic <ArrowRight /></>}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default OperatingModel;